import React, { useState } from 'react';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import './ContactForm.css';
import Spinner from './Spinner';
import Notification from './Notification';
import { useTracking } from '../hooks/useTracking';

const DEFAULT_VERIFY_RECAPTCHA_URL =
  'https://us-central1-alegra-club.cloudfunctions.net/verifyReCaptcha';

const ContactForm = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({
    message: '',
    show: false,
    type: 'success'
  });

  const { executeRecaptcha } = useGoogleReCaptcha();
  const { trackLead } = useTracking();

  const verifyRecaptchaUrl =
    process.env.REACT_APP_VERIFY_RECAPTCHA_URL || DEFAULT_VERIFY_RECAPTCHA_URL;

  const showNotification = (messageText, type = 'error') => {
    setNotification({ message: messageText, show: true, type });
  };

  const hideNotification = () => {
    setNotification((prev) => ({ ...prev, show: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let recaptchaToken = null;
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('submit_contact');
        } catch (error) {
          console.error('Error generating reCAPTCHA token:', error);
          showNotification('Error de reCAPTCHA, por favor intente nuevamente.', 'error');
          setIsLoading(false);
          return;
        }
      }

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(email)) {
        showNotification('Dirección de correo electrónico no válida.', 'error');
        setIsLoading(false);
        return;
      }

      if (!name.trim() || !email.trim() || !message.trim()) {
        showNotification('Por favor, complete todos los campos.', 'warning');
        setIsLoading(false);
        return;
      }

      let recaptchaValid = true;
      if (recaptchaToken) {
        try {
          const response = await fetch(verifyRecaptchaUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ token: recaptchaToken })
          });

          if (response.ok) {
            const result = await response.json();
            recaptchaValid = result.success;
          } else {
            console.warn('reCAPTCHA verification failed, but continuing...');
          }
        } catch (error) {
          console.warn('reCAPTCHA server not available, continuing without verification:', error);
        }
      }

      if (!recaptchaValid) {
        showNotification('Error de verificación reCAPTCHA. Por favor, intente nuevamente.', 'error');
        setIsLoading(false);
        return;
      }

      const contactData = {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        timestamp: new Date(),
        recaptchaToken: recaptchaToken || 'no-token',
        isArchived: false,
        status: 'nuevo',
        userAgent: navigator.userAgent,
        ip: 'unknown',
        emailsSent: false,
        customerEmailSent: false,
        adminEmailSent: false
      };

      const docRef = await addDoc(collection(db, 'contacts'), contactData);
      console.log('Contact saved with ID:', docRef.id);

      setName('');
      setEmail('');
      setMessage('');
      trackLead();
      showNotification(
        '¡Mensaje enviado correctamente! Recibirás una confirmación por email y te contactaremos pronto.',
        'success'
      );
    } catch (error) {
      console.error('Error al enviar el mensaje:', error);

      if (error.code === 'permission-denied') {
        showNotification('Error de permisos. Por favor, contacte al administrador.', 'error');
      } else if (error.message.includes('network')) {
        showNotification('Error de conexión. Verifique su internet e intente nuevamente.', 'error');
      } else {
        showNotification(
          'Error al enviar el mensaje. Por favor, intente nuevamente o contacte por email.',
          'error'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
  };

  if (isLoading) {
    return (
      <div className="cyber-shell cyber-shell--narrow contact-cyber contact-cyber--loading">
        <div className="cyber-shell__frame" aria-hidden="true" />
        <Spinner />
        <p className="contact-cyber__loading-msg">Enviando mensaje…</p>
      </div>
    );
  }

  return (
    <div className="cyber-shell cyber-shell--narrow contact-cyber">
      <div className="cyber-shell__frame" aria-hidden="true" />
      <h1 className="cyber-shell__title">Contacto</h1>
      <p className="cyber-shell__subtitle">Canal directo · respuesta en 24–48 h hábiles</p>

      <form className="contact-cyber__form" onSubmit={handleSubmit}>
        <div className="contact-cyber__field">
          <label className="contact-cyber__label" htmlFor="name">
            Nombre *
          </label>
          <input
            className="contact-cyber__input"
            type="text"
            id="name"
            value={name}
            onChange={handleInputChange(setName)}
            maxLength={100}
            required
            placeholder="Ingresá tu nombre"
          />
        </div>

        <div className="contact-cyber__field">
          <label className="contact-cyber__label" htmlFor="email">
            Email *
          </label>
          <input
            className="contact-cyber__input"
            type="email"
            id="email"
            value={email}
            onChange={handleInputChange(setEmail)}
            maxLength={100}
            required
            placeholder="tu.email@ejemplo.com"
          />
        </div>

        <div className="contact-cyber__field">
          <label className="contact-cyber__label" htmlFor="message">
            Mensaje *
          </label>
          <textarea
            className="contact-cyber__textarea"
            id="message"
            value={message}
            onChange={handleInputChange(setMessage)}
            required
            maxLength={1000}
            rows={5}
            placeholder="Escribí tu consulta…"
          />
          <small className="contact-cyber__meta">
            {message.length}/1000 caracteres
          </small>
        </div>

        <div className="contact-cyber__actions">
          <button
            type="submit"
            className="contact-cyber__submit"
            disabled={isLoading || !name.trim() || !email.trim() || !message.trim()}
          >
            <span className="contact-cyber__submit-inner">
              {isLoading ? 'Enviando…' : 'Enviar mensaje'}
            </span>
          </button>
        </div>
      </form>

      <footer className="contact-cyber__footer">
        <p className="contact-cyber__fineprint">* Campos obligatorios</p>
        <p className="contact-cyber__fineprint">
          Tus datos se usan solo para responder esta consulta.
        </p>
        <div className="contact-cyber__neon-tip" role="note">
          Al enviar recibís confirmación por correo y el equipo recibe el aviso.
        </div>
      </footer>

      <Notification
        message={notification.message}
        show={notification.show}
        type={notification.type}
        onClose={hideNotification}
        duration={5000}
      />
    </div>
  );
};

export default ContactForm;
